<template>
  <v-container class="py-6" max-width="1200">
    <!-- Header -->
    <div class="d-flex align-center mb-6">
      <v-icon color="primary" size="32" class="mr-3">mdi-shield-crown-outline</v-icon>
      <div>
        <h1 class="text-h5 font-weight-bold">Admin Dashboard</h1>
        <p class="text-body-2 text-medium-emphasis mb-0">Backgammon Tracker Overview</p>
      </div>
    </div>

    <!-- Quick Nav -->
    <v-row class="mb-6">
      <v-col cols="12" sm="3">
        <v-card variant="tonal" color="primary" to="/admin/users" hover>
          <v-card-text class="d-flex align-center pa-4">
            <v-icon size="40" class="mr-4">mdi-account-group</v-icon>
            <div>
              <div class="text-h6 font-weight-bold">Users</div>
              <div class="text-body-2">Manage users</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="3">
        <v-card variant="tonal" color="success" to="/admin/subscriptions" hover>
          <v-card-text class="d-flex align-center pa-4">
            <v-icon size="40" class="mr-4">mdi-credit-card-outline</v-icon>
            <div>
              <div class="text-h6 font-weight-bold">Subscriptions</div>
              <div class="text-body-2">Overview of payments</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="3">
        <v-card variant="tonal" color="warning" to="/admin/settings" hover>
          <v-card-text class="d-flex align-center pa-4">
            <v-icon size="40" class="mr-4">mdi-cog-outline</v-icon>
            <div>
              <div class="text-h6 font-weight-bold">Settings</div>
              <div class="text-body-2">Configuration</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="3">
        <v-card variant="tonal" color="info" to="/admin/training" hover>
          <v-card-text class="d-flex align-center pa-4">
            <v-icon size="40" class="mr-4">mdi-brain</v-icon>
            <div>
              <div class="text-h6 font-weight-bold">AI Training</div>
              <div class="text-body-2">Videoes & dice model</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="3">
        <v-card variant="tonal" color="deep-purple" to="/admin/kanban" hover>
          <v-card-text class="d-flex align-center pa-4">
            <v-icon size="40" class="mr-4">mdi-view-column-outline</v-icon>
            <div>
              <div class="text-h6 font-weight-bold">Dev Board</div>
              <div class="text-body-2">Tasks & ideas</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="3">
        <v-card variant="tonal" color="success" to="/admin/analysis" hover>
          <v-card-text class="d-flex align-center pa-4">
            <v-icon size="40" class="mr-4">mdi-motion-play-outline</v-icon>
            <div>
              <div class="text-h6 font-weight-bold">Analysis</div>
              <div class="text-body-2">Dice detection pipeline</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Stats Cards -->
    <v-row class="mb-6">
      <v-col v-for="stat in stats" :key="stat.label" cols="6" md="3">
        <v-card variant="outlined">
          <v-card-text class="text-center pa-4">
            <v-icon :color="stat.color" size="28" class="mb-2">{{ stat.icon }}</v-icon>
            <div class="text-h5 font-weight-bold">{{ stat.value }}</div>
            <div class="text-caption text-medium-emphasis">{{ stat.label }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Training Stats (quick glance) -->
    <v-row class="mb-6" v-if="trainingStats">
      <v-col cols="6" md="3">
        <v-card variant="outlined">
          <v-card-text class="text-center pa-4">
            <v-icon color="info" size="28" class="mb-2">mdi-video-outline</v-icon>
            <div class="text-h5 font-weight-bold">{{ trainingStats.totalVideos }}</div>
            <div class="text-caption text-medium-emphasis">Videos</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" md="3">
        <v-card variant="outlined">
          <v-card-text class="text-center pa-4">
            <v-icon color="info" size="28" class="mb-2">mdi-image-multiple-outline</v-icon>
            <div class="text-h5 font-weight-bold">{{ trainingStats.totalFrames }}</div>
            <div class="text-caption text-medium-emphasis">Frames in total</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" md="3">
        <v-card variant="outlined">
          <v-card-text class="text-center pa-4">
            <v-icon color="success" size="28" class="mb-2">mdi-check-circle-outline</v-icon>
            <div class="text-h5 font-weight-bold">{{ trainingStats.labeledFrames }}</div>
            <div class="text-caption text-medium-emphasis">Labeled frames</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" md="3">
        <v-card variant="outlined">
          <v-card-text class="text-center pa-4">
            <v-icon color="warning" size="28" class="mb-2">mdi-dice-multiple</v-icon>
            <div class="text-h5 font-weight-bold">{{ trainingStats.totalBoxes }}</div>
            <div class="text-caption text-medium-emphasis">Dice marked</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Recent Users -->
    <v-card variant="outlined">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2" size="20">mdi-account-clock</v-icon>
        Recent registrations
      </v-card-title>
      <v-divider />
      <v-table v-if="recentUsers.length" density="comfortable">
        <thead>
          <tr>
            <th>User</th>
            <th>Email</th>
            <th>Created</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="u in recentUsers" :key="u.userId">
            <td class="font-weight-medium">{{ u.displayName || u.username }}</td>
            <td>{{ u.email }}</td>
            <td>{{ formatDate(u.joinDate) }}</td>
            <td>
              <v-chip
                size="small"
                :color="u.subscription?.status === 'active' ? 'success' : 'grey'"
                variant="tonal"
              >
                {{ u.subscription?.status === 'active' ? 'Subscriber' : 'Free' }}
              </v-chip>
              <v-chip v-if="u.isAdmin" size="small" color="primary" variant="tonal" class="ml-1">
                Admin
              </v-chip>
              <v-chip v-else-if="u.isModerator" size="small" color="orange" variant="tonal" class="ml-1">
                Moderator
              </v-chip>
            </td>
          </tr>
        </tbody>
      </v-table>
      <v-card-text v-else-if="loading" class="text-center py-8">
        <v-progress-circular indeterminate color="primary" />
      </v-card-text>
      <v-card-text v-else class="text-center text-medium-emphasis py-8">
        No data available yet. API endpoints must be implemented.
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { getDashboardStats, getUsers } from "../../services/adminApi";
import api from "../../services/api";

const loading = ref(false);
const recentUsers = ref([]);
const trainingStats = ref(null);
const stats = ref([
  { label: "Total Users", value: "–", icon: "mdi-account-group", color: "primary" },
  { label: "Active Subscribers", value: "–", icon: "mdi-credit-card-check", color: "success" },
  { label: "Games in Total", value: "–", icon: "mdi-dice-multiple", color: "info" },
  { label: "New This Week", value: "–", icon: "mdi-account-plus", color: "warning" },
]);

function formatDate(dateStr) {
  if (!dateStr) return "–";
  return new Date(dateStr).toLocaleDateString('da-DK', {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

onMounted(async () => {
  loading.value = true;
  try {
    const { data: statsData } = await getDashboardStats();
    if (statsData) {
      stats.value[0].value = statsData.totalUsers ?? "–";
      stats.value[1].value = statsData.activeSubscribers ?? "–";
      stats.value[2].value = statsData.totalGames ?? "–";
      stats.value[3].value = statsData.newUsersThisWeek ?? "–";
    }
  } catch {}

  try {
    const { data } = await getUsers({ limit: 5, sort: "newest" });
    recentUsers.value = data.users || [];
  } catch {}

  // Load training stats
  try {
    const { data } = await api.get("/training/frames");
    trainingStats.value = data.stats || null;
  } catch {
    // Training API not available yet
  }

  loading.value = false;
});
</script>